/**
 * CarrosselPecasFita — carrossel read-only dos desenhos das peças da
 * solicitação, sinalizando onde há fita de borda. Uma peça por slide, com
 * setas + bolinhas de navegação e ANIMAÇÃO DE ROLAGEM lateral (o trilho
 * desliza na horizontal ao trocar de peça). Usado no detalhe (Gestão de
 * Solicitações).
 *
 * Seletor 2D/3D no canto superior esquerdo (pedido do dono, 2026-08-13):
 * padrão 2D; o 3D reusa o `Peca3D` da página pública da peça, carregado
 * TARDIAMENTE — o three.js (~570 KB) só baixa quando alguém liga o 3D, e o
 * chunk é o mesmo da página pública. Em 3D, só o slide VISÍVEL monta WebGL:
 * um contexto por slide estouraria o limite de contextos do navegador numa
 * solicitação com muitas peças.
 */
import React, { Suspense, lazy, useEffect, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

import VisualPecaFita from "../Solicitacoes/VisualPecaFita";
import { descreverFitas } from "../Solicitacoes/utils";
import {
  CarrosselCaption,
  CarrosselDot,
  CarrosselDots,
  CarrosselNavBtn,
  CarrosselPalco,
  CarrosselSlideItem,
  CarrosselTrack,
  CarrosselViewport,
  CarrosselWrap,
  SeletorVisao,
} from "./styles";

const Peca3D = lazy(() => import("../../PecaPublica/Peca3D"));

function LegendaPeca({ peca, indice, total }) {
  const fitas = descreverFitas(peca);
  return (
    <CarrosselCaption>
      <div className="titulo">
        {total > 1 ? `Peça ${indice + 1} de ${total} — ` : ""}
        {peca.descricao || "Peça"}
      </div>
      <div>
        {fitas ? (
          <span className="fita">Fita: {fitas}</span>
        ) : (
          <span className="sem">Sem fita de borda</span>
        )}
      </div>
    </CarrosselCaption>
  );
}

export default function CarrosselPecasFita({ pecas }) {
  const lista = pecas || [];
  const [i, setI] = useState(0);
  // Padrão 2D: é o desenho que a equipe já conhece; o 3D é opt-in.
  const [modo, setModo] = useState("2d");

  // Volta ao início quando muda a solicitação (lista nova).
  useEffect(() => {
    setI(0);
  }, [pecas]);

  if (!lista.length) return null;

  const atual = Math.min(i, lista.length - 1);
  const varias = lista.length > 1;

  return (
    <CarrosselWrap>
      <SeletorVisao role="group" aria-label="Modo de visualização da peça">
        <button
          type="button"
          data-on={modo === "2d"}
          onClick={() => setModo("2d")}
        >
          2D
        </button>
        <button
          type="button"
          data-on={modo === "3d"}
          onClick={() => setModo("3d")}
        >
          3D
        </button>
      </SeletorVisao>
      <CarrosselPalco>
        {varias && (
          <CarrosselNavBtn
            type="button"
            aria-label="Peça anterior"
            disabled={atual === 0}
            onClick={() => setI((v) => Math.max(0, v - 1))}
          >
            <FiChevronLeft size={18} />
          </CarrosselNavBtn>
        )}
        <CarrosselViewport>
          <CarrosselTrack style={{ transform: `translateX(-${atual * 100}%)` }}>
            {lista.map((peca, idx) => (
              <CarrosselSlideItem
                key={peca.id || idx}
                aria-hidden={idx !== atual}
              >
                {modo === "3d" && idx === atual ? (
                  <Suspense
                    fallback={
                      <div style={{ height: 380, display: "flex",
                        alignItems: "center", justifyContent: "center",
                        color: "var(--ink-2)", fontSize: 13 }}>
                        Preparando o 3D…
                      </div>
                    }
                  >
                    <Peca3D peca={peca} altura="380px" />
                  </Suspense>
                ) : (
                  <VisualPecaFita peca={peca} />
                )}
                <LegendaPeca peca={peca} indice={idx} total={lista.length} />
              </CarrosselSlideItem>
            ))}
          </CarrosselTrack>
        </CarrosselViewport>
        {varias && (
          <CarrosselNavBtn
            type="button"
            aria-label="Próxima peça"
            disabled={atual === lista.length - 1}
            onClick={() => setI((v) => Math.min(lista.length - 1, v + 1))}
          >
            <FiChevronRight size={18} />
          </CarrosselNavBtn>
        )}
      </CarrosselPalco>

      {varias && (
        <CarrosselDots>
          {lista.map((p, idx) => (
            <CarrosselDot
              key={p.id || idx}
              type="button"
              $ativo={idx === atual}
              aria-label={`Ir para a peça ${idx + 1}`}
              onClick={() => setI(idx)}
            />
          ))}
        </CarrosselDots>
      )}
    </CarrosselWrap>
  );
}
